import React, { useEffect, useMemo, useState } from "react";
import { Eye, Pen, Plus, Trash2, Mail } from "lucide-react";
import { useSelector } from "react-redux";

import { useOutletContext } from "react-router-dom";

import { DataTableNew } from "@/components/shared/DataTable";
import { Button } from "@/components/ui/Button";
import SearchInput from "@/components/shared/SearchInput";
import { Card } from "@/components/ui/Card";

import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";

import toast from "react-hot-toast";
import Skeleton from "@/components/shared/Skeleton";

import { RoleService } from "@/services/roleService";

import AddEditRoleModal from "./components/AddEditRoleModal";
import DeleteRoleModal from "./components/DeleteRoleModal";
import ViewRoleModal from "./components/ViewRoleModal";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import CustomTooltip from "@/components/shared/CustomTooltip";
import TenantScopeSelect from "@/components/shared/TenantScopeSelect";
import TableSkeleton from "@/components/shared/TableSkeleton";
import { useTenantScope } from "@/hooks/useTenantScope";

const RolePage = () => {
    // Set page header from layout (react-router outlet context)
    const setHeader = useOutletContext();

    // The role assigned to the currently signed-in user. The row matching
    // this id can be viewed but not edited or deleted — admins can't yank
    // out from under themselves the very role that grants them access.
    const currentUserRoleId = useSelector((state) => state.auth?.user?.roleId);

    // A SuperAdmin owns no tenant, so the page needs one named explicitly.
    // Unscoped it merged every shared tenant's roles — each name once per
    // tenant — and could not reach a dedicated tenant's roles at all.
    const {
        isSuperAdmin,
        tenants,
        tenantId,
        setTenantId,
        isLoading: tenantsLoading,
        isReady: scopeReady,
        scopeParams,
    } = useTenantScope();

    // Roles belong to the tenant's admin. A SuperAdmin looks at any tenant's
    // roles here and changes none of them; the API refuses the writes too.
    const canManage = !isSuperAdmin;

    // Search input state
    const [search, setSearch] = useState("");

    // Data table silent fetch
    const [silentFetch, setSilentFetch] = useState(false);

    // Table metadata (total + loading state)
    const [tableData, setTableData] = useState({ total: 0, isLoading: false });

    // Controls which modal is open: add | edit | delete | view
    const [activeModal, setActiveModal] = useState(null);

    // Currently selected role (for view/edit actions)
    const [selectedRole, setSelectedRole] = useState(null);

    // Role ID selected for deletion
    const [deletingRoleId, setDeletingRoleId] = useState(null);

    // Update header whenever table data changes
    useEffect(() => {
        setHeader({
            title: "Roles List",
            subTitle: tableData.isLoading ? (
                <Skeleton className="h-5 w-28" />
            ) : (
                `Total ${tableData.total} roles`
            ),
        });

        // Cleanup header on unmount
        return () =>
            setHeader({
                title: "",
                subTitle: "",
            });
    }, [tableData, setHeader]);

    useEffect(() => {
        if (silentFetch) {
            setTimeout(() => {
                setSilentFetch(false)
            }, 200)
        }
    }, [silentFetch])

    // Callback from DataTable when API data is loaded
    const onDataFetch = (data) => {
        setTableData(data);
    };

    // Default form values for Add/Edit modal
    const defaultValuesForModal = useMemo(() => {
        if (activeModal === "edit" && selectedRole) {
            return {
                name: selectedRole.name ?? "",
                parentRoleId: selectedRole?.parentRole?.id ?? "",
                departmentScope: selectedRole.departmentScope ?? "",
                description: selectedRole.description ?? "",
                permissions: selectedRole?.permissions ?? [],
            };
        }

        // Default empty values for "add" mode
        return {
            name: "",
            parentRoleId: "",
            departmentScope: "",
            description: "",
            permissions: [],
        };
    }, [activeModal, selectedRole]);

    // Handle create/update role API call
    const handleSaveRole = async (formData) => {
        try {
            const payload = {
                ...formData,
                parentRoleId: formData.parentRoleId || null,
                manager: formData.manager || null,
            };

            // Update existing role
            if (activeModal === "edit" && selectedRole?.id) {
                await RoleService.updateRole(selectedRole.id, payload);

                toast.success("Role updated");
            } else {
                // Create new role
                await RoleService.createRole(payload);

                toast.success("Role added");
            }

            // Reset modal state after success
            setActiveModal(null);
            setSelectedRole(null);
            setSilentFetch(true);
        } catch (e) {
            console.error(e);

            toast.error(apiErrorMessageFormat(e, `Failed to ${activeModal === "add" ? "save" : "update"} role`));
        }
    };

    // Table column definitions
    const COLUMNS = [
        {
            key: "name",
            label: "Name",
            sortable: true,
            align: "left",
            render: (val) => (
                <span className="text-left font-medium text-[13px]">
                    {val ?? "—"}
                </span>
            ),
        },
        {
            // Not sortable: `GET /roles` takes name, created_at, updated_at
            // and userCount, and `DataTableNew` sends the column's own key. A
            // click on this header put `parentRole` on the wire, FastAPI
            // answered 422, and the table was replaced by "This table could
            // not be loaded." until the admin found their way back via Name.
            // Add `sortable: true` back only with a `sortKey` the route takes
            // (see super-admin/permissions/index.jsx for that pattern).
            key: "parentRole",
            label: "Parent Role",
            align: "left",
            render: (_val, row) => (
                <span className="text-left font-medium text-[13px]">
                    {row?.parentRole?.name || "—"}
                </span>
            ),
        },
        {
            key: "departmentScope",
            label: "Department",
            align: "left",
            render: (val) => (
                <span className="text-left font-medium text-[13px]">
                    {val ?? "—"}
                </span>
            ),
        },
        {
            key: "description",
            label: "Description",
            wrap: true,
            align: "left",
            style: { width: '35%' },
            className: 'truncate',
            render: (val) => (
                <span className="text-left font-medium text-[13px] ">
                    {val ?? "—"}
                </span>
            ),
        },
        {
            key: "permissions",
            label: "Permission",
            wrap: true,
            align: "left",
            render: (val) => {
                const list = Array.isArray(val) ? val : [];

                if (!list.length) return <span>—</span>;

                return (
                    <span className="text-left font-medium text-[13px]">
                        {list.length} permission{list.length > 1 ? "s" : ""}
                    </span>
                );
            },
        },
        {
            key: "id",
            label: "Actions",
            sortable: false,
            align: "center",
            render: (_val, row) => {
                const isOwnRole = !!currentUserRoleId && row?.id === currentUserRoleId;
                const ownRoleTooltip = isOwnRole
                    ? "You cannot edit or delete your own role"
                    : undefined;
                // The role every new AD sign-in is handed. The API refuses to
                // delete it (CANNOT_MODIFY_DEFAULT_ROLE), so the button says so
                // instead of failing when pressed. Editing it is still fine —
                // only the row's existence is depended on.
                const isSignInDefault = !!row?.isDefaultAssignment;
                const deleteTooltip = ownRoleTooltip
                    || (isSignInDefault
                        ? "This is the default role for new sign-ins and cannot be deleted"
                        : undefined);
                return (
                    <div className="flex gap-2 justify-center">
                        {/* View role */}
                        <PermissionGuard permissions={[PERMISSIONS.ROLE_VIEW, PERMISSIONS.SUPER_ADMIN_MANAGE]} isComponent>
                            <CustomTooltip  text="View Role">
                                <Button
                                    aria-label="View role"
                                    onClick={() => {
                                        setSelectedRole(row);
                                        setActiveModal("view");
                                    }}
                                    className="px-4 py-3 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60"
                                >
                                    <Eye size={16} />
                                </Button>
                            </CustomTooltip>
                        </PermissionGuard>

                        {/* Edit role */}
                        {canManage && (
                            <PermissionGuard permissions={[PERMISSIONS.ROLE_UPDATE, PERMISSIONS.ROLE_MANAGE]} isComponent>
                                <CustomTooltip  text="Edit Role">
                                    <Button
                                        aria-label="Edit role"
                                        onClick={() => {
                                            setSelectedRole(row);
                                            setActiveModal("edit");
                                        }}
                                        disabled={isOwnRole}
                                        title={ownRoleTooltip}
                                        className="px-4 py-3 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background-icon"
                                    >
                                        <Pen size={16} />
                                    </Button>
                                </CustomTooltip>
                            </PermissionGuard>
                        )}

                        {/* Delete role */}
                        {canManage && (
                            <PermissionGuard permissions={[PERMISSIONS.ROLE_DELETE, PERMISSIONS.ROLE_MANAGE]} isComponent>
                                <CustomTooltip  text="Delete Role">
                                    <Button
                                        aria-label="Delete role"
                                        onClick={() => {
                                            setDeletingRoleId(row?.id);
                                            setActiveModal("delete");
                                        }}
                                        disabled={isOwnRole || isSignInDefault}
                                        title={deleteTooltip}
                                        className="px-4 py-3 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background-icon"
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                </CustomTooltip>
                            </PermissionGuard>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <PermissionGuard permissions={[PERMISSIONS.ROLE_VIEW, PERMISSIONS.SUPER_ADMIN_MANAGE]}>
            <div className="flex flex-col gap-2.5" style={{ zoom: 0.85 }}>

                {/* Search + Add Role */}
                <div className="flex items-center gap-2.5">
                    <div className="flex-1">
                        <SearchInput
                            value={search}
                            onChange={setSearch}
                            placeholder="Search by name, department, description..."
                        />
                    </div>

                    <TenantScopeSelect
                        isSuperAdmin={isSuperAdmin}
                        tenants={tenants}
                        tenantId={tenantId}
                        onChange={setTenantId}
                        isLoading={tenantsLoading}
                    />

                    {canManage && (
                        <PermissionGuard permissions={[PERMISSIONS.ROLE_CREATE, PERMISSIONS.ROLE_MANAGE]} isComponent>
                            <Button
                                onClick={() => {
                                    setSelectedRole(null);
                                    setActiveModal("add");
                                }}
                                className="hidden md:flex bg-button-secondary text-text-base rounded-full px-3 py-2 items-center gap-1 text-[11px] whitespace-nowrap shrink-0"
                            >
                                <Plus size={11} />
                                Add Role
                            </Button>
                        </PermissionGuard>
                    )}
                </div>

                {/* Mobile FAB */}
                <div className="fixed bottom-0 left-0 right-0 z-[110] md:hidden px-4 py-3">
                    {canManage && (
                        <PermissionGuard permissions={[PERMISSIONS.ROLE_CREATE, PERMISSIONS.ROLE_MANAGE]} isComponent>
                            <Button
                                onClick={() => {
                                    setSelectedRole(null);
                                    setActiveModal("add");
                                }}
                                className="w-full bg-button-secondary text-text-base rounded-full py-2.5 flex items-center justify-center gap-1.5 text-[13px]"
                            >
                                <Plus size={14} />
                                Add Role
                            </Button>
                        </PermissionGuard>
                    )}
                </div>

                {/* Table. Held until a SuperAdmin has a tenant, so the first
                    request is never an unscoped cross-tenant read. The key
                    carries the tenant: a new scope is a new list. */}
                <Card className="p-0 bg-table-heading border-border-primary">
                    {scopeReady ? (
                        <DataTableNew
                            key={`roles-list-${tenantId || "self"}`}
                            className="rounded-none"
                            columns={COLUMNS}
                            showPagination={true}
                            sortable={true}
                            api={RoleService.fetchRolesList}
                            paginationLabel="roles"
                            search={search}
                            dataPath="roles"
                            onDataFetch={onDataFetch}
                            silentFetch={silentFetch}
                            extraParams={scopeParams}
                        />
                    ) : (
                        <TableSkeleton />
                    )}
                </Card>

                {/* Add/Edit Modal */}
                <AddEditRoleModal
                    isOpen={activeModal === "add" || activeModal === "edit"}
                    mode={activeModal === "edit" ? "edit" : "add"}
                    // So the Parent Role dropdown can leave out this role and
                    // everything below it — the two things the route refuses.
                    editRoleId={activeModal === "edit" ? selectedRole?.id ?? null : null}
                    onModelClose={() => {
                        setActiveModal(null);
                        setSelectedRole(null);
                    }}
                    onHandleSubmit={handleSaveRole}
                    defaultValues={defaultValuesForModal}
                />

                {/* Delete Modal */}
                <DeleteRoleModal
                    open={activeModal === "delete"}
                    onModelClose={() => {
                        setActiveModal(null);
                        setDeletingRoleId(null);
                    }}
                    roleId={deletingRoleId}
                    loading={false}
                    setSilentFetch={setSilentFetch}
                />

                {/* View Modal */}
                <ViewRoleModal
                    open={activeModal === "view"}
                    onModelClose={() => {
                        setActiveModal(null);
                        setSelectedRole(null);
                    }}
                    role={selectedRole}
                />
            </div>
        </PermissionGuard>
    );
};

export default RolePage;